import React, { useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';
import { ensureCurrentUser } from '../../../util/data';

import { Avatar, NamedLink } from '../../../components';

import css from './LayoutSideNavigation.module.css';

const getProfileDisplayName = user => {
  const profile = user?.attributes?.profile || {};
  const fallbackName = [profile.firstName, profile.lastName].filter(Boolean).join(' ');
  return profile.displayName || fallbackName || 'Your profile';
};

const getMemberSince = user => {
  const createdAt = user?.attributes?.createdAt;
  if (!createdAt) {
    return 'Member profile';
  }

  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) {
    return 'Member profile';
  }

  return `Member since ${date.toLocaleDateString('en', { month: 'short', year: 'numeric' })}`;
};

const AccountNavLink = props => {
  const { children, currentPage, icon, name, selected, suppressActive, id } = props;
  const isSelected = !suppressActive && (selected || currentPage === name);

  return (
    <NamedLink
      id={id}
      className={classNames(css.accountNavLink, { [css.accountNavLinkActive]: isSelected })}
      name={name}
    >
      <span className={css.accountNavIcon}>{icon}</span>
      <span>{children}</span>
    </NamedLink>
  );
};

const AccountNavGroup = props => {
  const { children, title } = props;
  return (
    <div className={css.accountNavGroup}>
      <div className={css.accountNavEyebrow}>{title}</div>
      {children}
    </div>
  );
};

const AccountNavMenuIcon = props => {
  const { className } = props;
  return (
    <svg
      className={className}
      width="18"
      height="12"
      viewBox="0 0 18 12"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <g fillRule="evenodd">
        <rect width="18" height="2" rx="1" />
        <rect y="5" width="18" height="2" rx="1" />
        <rect y="10" width="18" height="2" rx="1" />
      </g>
    </svg>
  );
};

/**
 * Side nav with navigation to profile and account settings.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.accountSettingsNavProps
 * @param {string?} props.accountSettingsNavProps.currentPage
 * @param {boolean?} props.accountSettingsNavProps.showManageListingsLink
 * @param {boolean?} props.accountSettingsNavProps.showPaymentMethods
 * @param {boolean?} props.accountSettingsNavProps.showPayoutDetails
 * @param {Object?} props.accountSettingsNavProps.currentUser
 * @returns {JSX.Element} Side nav with navigation to profile and account settings
 */
const LayoutWrapperAccountSettingsSideNav = props => {
  const { accountSettingsNavProps, ariaLabel, id } = props;
  const [isOpenOnMobile, setIsOpenOnMobile] = useState(false);
  const {
    currentPage,
    currentUser,
    showManageListingsLink,
    showPaymentMethods,
    showPayoutDetails,
    userTypeLabel,
  } = accountSettingsNavProps || {};

  const user = ensureCurrentUser(currentUser);
  const displayName = getProfileDisplayName(user);
  const memberSince = getMemberSince(user);
  const navId = id || 'account-settings-navigation';

  return (
    <div
      className={classNames(css.accountNavWrapper, {
        [css.accountNavWrapperOpen]: isOpenOnMobile,
      })}
    >
      <button
        className={css.accountNavToggle}
        type="button"
        aria-controls={navId}
        aria-expanded={isOpenOnMobile}
        aria-label={isOpenOnMobile ? 'Close account menu' : 'Open account menu'}
        onClick={() => setIsOpenOnMobile(!isOpenOnMobile)}
      >
        <AccountNavMenuIcon className={css.accountNavToggleIcon} />
      </button>

      <nav id={navId} className={css.accountNavCard} aria-label={ariaLabel}>
        <AccountNavGroup title="Account">
          {showManageListingsLink ? (
            <AccountNavLink
              currentPage={currentPage}
              icon="L"
              id="ManageListingsPageTab"
              name="ManageListingsPage"
            >
              <FormattedMessage id="UserNav.yourListings" />
            </AccountNavLink>
          ) : null}
          <AccountNavLink
            currentPage={currentPage}
            icon="P"
            id="ProfileSettingsPageTab"
            name="ProfileSettingsPage"
          >
            <FormattedMessage id="UserNav.profileSettings" />
          </AccountNavLink>
          <AccountNavLink
            currentPage={currentPage}
            icon="A"
            id="AccountSettingsPageTab"
            name="ContactDetailsPage"
            suppressActive
          >
            <FormattedMessage id="UserNav.accountSettings" />
          </AccountNavLink>
        </AccountNavGroup>

        <AccountNavGroup title="Account settings">
          <AccountNavLink
            currentPage={currentPage}
            icon="E"
            id="ContactDetailsPageTab"
            name="ContactDetailsPage"
          >
            <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.contactDetailsTabTitle" />
          </AccountNavLink>
          <AccountNavLink
            currentPage={currentPage}
            icon="K"
            id="PasswordChangePageTab"
            name="PasswordChangePage"
          >
            <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.passwordTabTitle" />
          </AccountNavLink>
          {showPayoutDetails ? (
            <AccountNavLink
              currentPage={currentPage}
              icon="B"
              id="StripePayoutPageTab"
              name="StripePayoutPage"
            >
              <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.paymentsTabTitle" />
            </AccountNavLink>
          ) : null}
          {showPaymentMethods ? (
            <AccountNavLink
              currentPage={currentPage}
              icon="C"
              id="PaymentMethodsPageTab"
              name="PaymentMethodsPage"
            >
              <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.paymentMethodsTabTitle" />
            </AccountNavLink>
          ) : null}
          <AccountNavLink
            currentPage={currentPage}
            icon="M"
            id="ManageAccountPageTab"
            name="ManageAccountPage"
          >
            <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.manageAccountTabTitle" />
          </AccountNavLink>
        </AccountNavGroup>

        {user.id ? (
          <div className={css.accountNavUser}>
            <Avatar className={css.accountNavAvatar} user={currentUser} disableProfileLink />
            <div className={css.accountNavUserText}>
              <div className={css.accountNavUserName}>{displayName}</div>
              <div className={css.accountNavUserMeta}>
                {userTypeLabel || 'Marketplace member'} - {memberSince}
              </div>
            </div>
          </div>
        ) : null}
      </nav>
    </div>
  );
};

export default LayoutWrapperAccountSettingsSideNav;
